export type PlanTierId = 'starter' | 'professional' | 'scale' | 'enterprise';

export interface PlanTier {
  id: PlanTierId;
  name: string;
  tagline: string;
  /** Monthly price in INR; null means custom / contact-sales pricing */
  monthlyPriceInr: number | null;
  /** Monthly price in USD; null means custom / contact-sales pricing */
  monthlyPriceUsd: number | null;
  features: string[];
  /** Visually emphasized tier in the pricing grid */
  highlighted: boolean;
  ctaLabel: string;
}

/** Single point of contact for plan upgrades until self-serve checkout exists */
export const SALES_EMAIL = 'sales@opsero.in';

/** Usage percentage at which we warn the user they are close to a limit */
export const USAGE_WARNING_THRESHOLD = 90;

export const PLAN_TIERS: PlanTier[] = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'For small agencies getting started',
    monthlyPriceInr: 4999,
    monthlyPriceUsd: 99,
    features: [
      '5 Seats',
      '5,000 Influencers in Database',
      '3,000 Profile Refreshes / month',
      '10 Active Campaigns',
      '3,000 Deliverables / month',
    ],
    highlighted: false,
    ctaLabel: 'Upgrade to Starter',
  },
  {
    id: 'professional',
    name: 'Professional',
    tagline: 'For growing agencies',
    monthlyPriceInr: 11999,
    monthlyPriceUsd: 299,
    features: [
      '15 Seats',
      '15,000 Influencers in Database',
      '10,000 Profile Refreshes / month',
      '25 Active Campaigns',
      '10,000 Deliverables / month',
      '1,500 Bulk Emails / month',
    ],
    highlighted: true,
    ctaLabel: 'Upgrade to Professional',
  },
  {
    id: 'scale',
    name: 'Scale',
    tagline: 'For established agencies',
    monthlyPriceInr: 29999,
    monthlyPriceUsd: 799,
    features: [
      '30 Seats',
      '50,000 Influencers in Database',
      '25,000 Profile Refreshes / month',
      'Unlimited Active Campaigns',
      '25,000 Deliverables / month',
      '5,000 Bulk Emails / month',
    ],
    highlighted: false,
    ctaLabel: 'Upgrade to Scale',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For large agencies & teams',
    monthlyPriceInr: null,
    monthlyPriceUsd: null,
    features: [
      'Unlimited Seats',
      'Unlimited Influencers in Database',
      'Custom Profile Refreshes',
      'Unlimited Deliverables',
      'Custom Bulk Emails',
      'Custom Reports',
      'Dedicated Account Manager',
    ],
    highlighted: false,
    ctaLabel: 'Book a Call',
  },
];

export const formatInr = (amount: number): string =>
  `\u20B9${amount.toLocaleString('en-IN')}`;

export const formatUsd = (amount: number): string =>
  `$${amount.toLocaleString('en-US')}`;

/**
 * Matches a subscription's stored plan name (e.g. "Professional Plan",
 * "professional") against a pricing tier.
 */
export const isCurrentPlan = (
  subscriptionPlanName: string | null | undefined,
  tier: PlanTier
): boolean => {
  if (!subscriptionPlanName) return false;
  const normalized = subscriptionPlanName
    .toLowerCase()
    .replace(/\s*plan\s*$/, '')
    .trim();
  return normalized === tier.name.toLowerCase();
};

export const upgradeMailtoHref = (tier: PlanTier): string => {
  const subject =
    tier.monthlyPriceInr === null
      ? 'Enterprise plan enquiry'
      : `Upgrade request: ${tier.name} plan`;
  return `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}`;
};
